"use client"

import { useEffect } from "react"
import posthog from "posthog-js"

// Segment-level error boundary for the public routes (book, survey, text,
// jobs). Without this, any render throw in a public page unwinds all the way
// to the root boundary; this contains it to the public segment and — more
// importantly — tags the PostHog capture with boundary:"public" so a
// public-page crash (e.g. the booking prep view) is attributable at triage
// time instead of being indistinguishable from any other root crash.
//
// Mirrors app/error.tsx's chunk-load auto-recovery: a user who keeps a public
// page open across a deploy then interacts can 404 on a stale hashed chunk;
// reload once (guarded against loops) to fetch the new hashes.
const RELOAD_SENTINEL = "ha-chunk-reload-attempt-public"

function looksLikeChunkLoad(error: Error): boolean {
  if (error.name === "ChunkLoadError") return true
  const haystack = `${error.message ?? ""} ${error.stack ?? ""}`
  return haystack.includes("/_next/static/chunks/")
}

export default function PublicErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    posthog.capture("react_error_boundary_triggered", {
      boundary: "public",
      error_name: error.name || "Error",
      // Without the message a crash is just "TypeError" (error-patrol 2026-10-15).
      error_message: error.message?.slice(0, 300),
      error_stack: error.stack?.slice(0, 1000),
      digest: error.digest,
    })
    if (typeof window === "undefined") return
    if (!looksLikeChunkLoad(error)) return
    if (sessionStorage.getItem(RELOAD_SENTINEL)) return
    sessionStorage.setItem(RELOAD_SENTINEL, "1")
    window.location.reload()
  }, [error])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-lg font-semibold">Something went wrong</h1>
      <p className="text-sm text-muted-foreground max-w-md">
        Try refreshing the page. If the problem keeps happening, contact
        support and include the code below.
      </p>
      {error.digest && (
        <code className="rounded bg-muted px-2 py-1 text-xs">{error.digest}</code>
      )}
      <button
        type="button"
        onClick={reset}
        className="mt-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Try again
      </button>
    </div>
  )
}
